import React from "react";
import { ArrowDownRight, ArrowUpRight, Info, Minus } from "lucide-react";
import { Card } from "../ui/card";
import { Skeleton } from "../ui/skeleton";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../ui/hover-card";
import { signedPct } from "../../lib/format";

function MetaTooltip({ label, formula, sources, note, testId }) {
  if (!formula && !sources && !note) return null;
  return (
    <HoverCard openDelay={120} closeDelay={80}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          aria-label={`How "${label}" is calculated`}
          data-testid={`${testId}-info`}
          className="opacity-50 hover:opacity-100 transition-opacity p-0.5 -m-0.5 rounded text-muted-foreground hover:text-foreground"
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        side="top"
        align="end"
        sideOffset={8}
        className="w-80 text-xs leading-relaxed"
      >
        <p className="font-display font-semibold text-sm mb-2">{label}</p>
        {formula && (
          <div className="mb-2">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
              Formula
            </p>
            <p>{formula}</p>
          </div>
        )}
        {sources && (
          <div className="mb-2">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
              Data sources
            </p>
            <p>{sources}</p>
          </div>
        )}
        {note && (
          <div className="mt-2 pt-2 border-t border-border/60">
            <p className="text-muted-foreground italic">{note}</p>
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}

export default function KpiCard({
  label,
  value,
  prev,
  changePct,
  format,
  invertChange = false,
  loading,
  testId,
  formula,
  sources,
  note,
  compareLabel = "vs prior period",
}) {
  const dir = changePct == null ? 0 : changePct === 0 ? 0 : changePct > 0 ? 1 : -1;
  const goodWhenUp = !invertChange;
  const positive = dir === 0 ? null : (dir > 0 ? goodWhenUp : !goodWhenUp);

  return (
    <Card
      className="p-5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md relative"
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-medium">
          {label}
        </p>
        <MetaTooltip
          label={label}
          formula={formula}
          sources={sources}
          note={note}
          testId={testId}
        />
      </div>
      {loading ? (
        <Skeleton className="h-9 w-28 mt-3" />
      ) : (
        <p className="mt-3 font-display text-3xl md:text-[2rem] font-semibold tracking-tight num">
          {format ? format(value) : value}
        </p>
      )}
      <div className="mt-3 flex items-center gap-2 text-xs">
        {loading ? (
          <Skeleton className="h-4 w-20" />
        ) : (
          <>
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium ${
                positive == null
                  ? "bg-muted text-muted-foreground"
                  : positive
                    ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                    : "bg-destructive/10 text-destructive"
              }`}
              data-testid={`${testId}-change`}
            >
              {dir === 0 ? (
                <Minus className="h-3 w-3" />
              ) : dir > 0 ? (
                <ArrowUpRight className="h-3 w-3" />
              ) : (
                <ArrowDownRight className="h-3 w-3" />
              )}
              {signedPct(changePct)}
            </span>
            {changePct != null && (
              <span className="text-muted-foreground" data-testid={`${testId}-compare-label`}>{compareLabel}</span>
            )}
          </>
        )}
      </div>
    </Card>
  );
}
